/**
 * Add or edit a team member.
 *
 * This writes real accounts — the `users` table the API signs people in
 * against — not the display-only list the settings JSON used to carry. So the
 * password field is doing something: whoever is created here can sign in with
 * it straight away, and removing them ends the session they are holding.
 */
import { useState } from 'react';
import { useApp } from '../../store/AppContext.jsx';
import Icon from '../../lib/icons.jsx';
import Sheet, { Group, Field, OptionSelect } from '../Sheet.jsx';

export default function TeamSheet({ user, roles, onClose, onSaved }) {
  const { S, toast } = useApp();
  const editing = !!(user && user.id);

  const [form, setForm] = useState(() => ({
    name: user?.name || '',
    email: user?.email || '',
    password: '',
    role: user?.role || 'Agent',
    dept: user?.dept || '',
    title: user?.title || '',
    phone: user?.phone || '',
    active: user ? user.active !== false : true
  }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [show, setShow] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    const name = String(form.name).trim();
    const email = String(form.email).trim().toLowerCase();

    if (!name) { setError('Give them a name'); return; }
    if (!email) { setError('An email address is how they sign in'); return; }
    /* Only on create, or when one has actually been typed: leaving it blank
       while editing means "keep the password they already have". */
    if ((!editing || form.password) && String(form.password).length < 8) {
      setError('Use a password of at least 8 characters');
      return;
    }

    setBusy(true);
    setError('');
    try {
      await onSaved({
        name,
        email,
        role: form.role,
        dept: form.dept,
        title: form.title,
        phone: form.phone,
        ...(editing ? { active: form.active } : {}),
        ...(form.password ? { password: form.password } : {})
      });
      toast(editing ? `${name} updated` : `${name} added`);
      onClose();
    } catch (e) {
      setError(e?.message || 'Could not save');
      setBusy(false);
    }
  };

  return (
    <Sheet
      icon="users"
      tone="g-purple"
      title={editing ? 'Edit team member' : 'Add a team member'}
      onClose={onClose}
      onSave={busy ? undefined : save}
      saveLabel={busy ? 'Saving…' : (editing ? 'Save changes' : 'Create account')}
    >
      <Group>Account</Group>

      <Field label="Full name" wide>
        <input autoFocus value={form.name} onChange={e => set('name', e.target.value)} />
      </Field>

      <Field label="Email" wide>
        <input
          type="email"
          autoComplete="off"
          value={form.email}
          onChange={e => set('email', e.target.value)}
        />
        <div className="muted">This is what they sign in with.</div>
      </Field>

      <Field label={editing ? 'New password' : 'Password'} wide>
        <div className="pwfield">
          <input
            type={show ? 'text' : 'password'}
            autoComplete="new-password"
            placeholder={editing ? 'Leave blank to keep the current one' : 'At least 8 characters'}
            value={form.password}
            onChange={e => set('password', e.target.value)}
          />
          <button
            type="button"
            className="pweye"
            aria-label={show ? 'Hide password' : 'Show password'}
            onClick={() => setShow(v => !v)}
          >
            <Icon name={show ? 'eyeoff' : 'eye'} />
          </button>
        </div>
        {editing ? (
          <div className="muted">Setting one signs them out of their other sessions.</div>
        ) : null}
      </Field>

      <Group>Role</Group>

      <Field label="Role">
        <OptionSelect value={form.role} onChange={v => set('role', v)} options={roles} />
      </Field>

      <Field label="Department">
        <OptionSelect
          value={form.dept}
          onChange={v => set('dept', v)}
          options={S.depts || []}
          blank="No department"
        />
      </Field>

      <Field label="Job title">
        <input value={form.title} onChange={e => set('title', e.target.value)} />
      </Field>

      <Field label="Phone">
        <input value={form.phone} onChange={e => set('phone', e.target.value)} />
      </Field>

      {editing ? (
        <Field label="Active" wide>
          <label className="chkrow">
            <input
              type="checkbox"
              className="chk"
              checked={form.active}
              onChange={e => set('active', e.target.checked)}
            />
            Can sign in
          </label>
          <div className="muted">
            Deactivating keeps their history and their name on the records they own,
            but they cannot sign in and the session they are holding ends.
          </div>
        </Field>
      ) : null}

      {error ? <div className="f wide"><div className="err">{error}</div></div> : null}
    </Sheet>
  );
}
